import React, { useState } from "react";
import { GlobalState } from "store/configure-store";
import {
  selectHelenaPatientUnsubscribed,
  selectRegistrationLink,
  selectSessionKey,
} from "store/reducers/helena.reducer";
import { connect, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectPatient } from "store/reducers/patient.reducer";
import { Patient } from "model/patient.model";
import { HelenaSessionKey } from "model/helena.model";
import { helenaRegistrationFinished } from "store/actions/helena.actions";
import { selectUser } from "store/reducers/user-context.reducer";
import { User } from "model/user.model";
import { Alert, Button, Col, Icon, Row } from "@cc/react-components";
import { SyncServiceType } from "model/patient-sync.model";
import { createPatientSync } from "store/actions/patient-sync.actions";
import { RegistrationFinishedType } from "store/types/helena.types";

interface Props {
  messageKey?: string;
}

const HelenaPatientRegistrationCard = ({
  helenaRegistrationFinished,
  messageKey,
}: Props & DispatchToPropsState) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const user = useSelector(selectUser);
  const patient = useSelector(selectPatient);
  const registrationLink = useSelector(selectRegistrationLink);
  const sessionKey = useSelector((state: GlobalState) =>
    selectSessionKey(state.helena)
  );
  const helenaPatientUnsubscribed = useSelector((state: GlobalState) =>
    selectHelenaPatientUnsubscribed(state.helena)
  );
  const [isRegistrationLinkOpened, setRegistrationLinkOpened] = useState(false);

  const renderHelenaPatientUnsubscribed = () => {
    return (
      <Alert variant="primary">
        <Row>
          <Col xs={2}>
            <Icon type="info" size="2x" />
          </Col>
          <Col>{t("helena.exclusion")}</Col>
        </Row>
      </Alert>
    );
  };

  const renderHelenaNotRegistered = () => {
    return (
      <>
        <Alert variant="primary">
          <Row>
            <Col xs={2}>
              <Icon type="info" size="2x" />
            </Col>
            <Col>
              <p>
                {t(!!messageKey ? messageKey : "helena.patient.medicationInfo")}
              </p>
              <p>{t("helena.patient.notRegistered.askToRegister")}</p>
            </Col>
          </Row>
        </Alert>
        <section>
          {registrationLink &&
            renderRegistrationLink(
              registrationLink,
              isRegistrationLinkOpened,
              setRegistrationLinkOpened
            )}
          {renderNeverRegisterPatient(user, patient)}
        </section>
        <section>
          {isRegistrationLinkOpened &&
            sessionKey &&
            renderRegistrationFinished(
              user,
              sessionKey,
              patient,
              isRegistrationLinkOpened
            )}
        </section>
      </>
    );
  };

  const renderRegistrationLink = (
    registrationLink: string,
    isRegistrationLinkOpened: boolean,
    setRegistrationLinkOpened: (boolModal: boolean) => void
  ) => {
    return (
      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          window.open(registrationLink, "_black", "rel='noopener noreferrer'");
          setRegistrationLinkOpened(true);
        }}
        active={isRegistrationLinkOpened}
      >
        {t("helena.patient.register")}
      </Button>
    );
  };

  const renderNeverRegisterPatient = (user: User, patient: Patient) => {
    return (
      <Button
        variant="primary"
        size="lg"
        block
        active={true}
        onClick={() => {
          dispatch(
            createPatientSync(
              user.id,
              patient.id,
              SyncServiceType.HELENA_VISIT_INVITATION
            )
          );
        }}
      >
        {t("helena.patient.neverRegister")}
      </Button>
    );
  };

  const renderRegistrationFinished = (
    user: User,
    sessionKey: HelenaSessionKey,
    patient: Patient,
    isRegistrationLinkOpened: boolean
  ) => {
    return (
      <Button
        active={isRegistrationLinkOpened}
        variant="secondary"
        size="lg"
        block
        onClick={() =>
          onRegistrationFinished(
            user,
            sessionKey,
            patient,
            helenaRegistrationFinished
          )
        }
      >
        {t("helena.patient.finished")}
      </Button>
    );
  };

  const onRegistrationFinished = (
    user: User,
    sessionKey: HelenaSessionKey,
    patient: Patient,
    helenaRegistrationFinished: RegistrationFinishedType
  ) => {
    if (patient.inss) {
      helenaRegistrationFinished(user.id, sessionKey, patient.inss).then(
        (pdf) => {
          if (pdf) {
            const binaryString = window.atob(pdf);
            const bytes = new Uint8Array(binaryString.length);
            const body = bytes.map((byte, i) => binaryString.charCodeAt(i));
            const blob = new Blob([body], { type: "application/pdf" });
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
              window.navigator.msSaveOrOpenBlob(blob);
              return;
            }
            const data = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = data;
            link.download = "file.pdf";
            link.click();
          }
        }
      );
    }
  };

  return (
    <React.Fragment>
      {!helenaPatientUnsubscribed && renderHelenaNotRegistered()}
      {helenaPatientUnsubscribed && renderHelenaPatientUnsubscribed()}
    </React.Fragment>
  );
};

interface DispatchToPropsState {
  helenaRegistrationFinished: RegistrationFinishedType;
}

export default connect(undefined, {
  helenaRegistrationFinished,
})(HelenaPatientRegistrationCard);
